import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaGoogle } from "react-icons/fa";
import { useLanguage } from "./LanguageContext";
import axios from "axios";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME ================= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  shadow: "0 20px 60px rgba(0,0,0,.12)",
};

/* ================= STYLES ================= */
const Page = styled.div`
  display: grid;
  place-items: start center;
  background: #fff;
`;

const Container = styled.div`
  width: min(540px, 92vw);
  margin: 18px auto 26px;
`;

const Card = styled.div`
  background: ${COLORS.bg};
  border: 1px solid ${COLORS.line};
  border-radius: 14px;
  box-shadow: ${COLORS.shadow};
  padding: 16px;
`;

const Header = styled.div`
  padding: 4px 2px 10px;
`;

const Title = styled.h1`
  margin: 0 0 2px;
  font-size: 18px;
  font-weight: 900;
  color: ${COLORS.text};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${COLORS.dim};
`;

const Section = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 12px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 800;
  color: ${COLORS.text};
`;

const TextInput = styled.input`
  width: 95%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  background: #fff;
  font-size: 14px;
  color: ${COLORS.text};
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
  &:focus { border-color: ${COLORS.brand}; box-shadow: 0 0 0 3px rgba(161,90,90,.15); }
`;

const Select = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  background: #fff;
  font-size: 14px;
  color: ${COLORS.text};
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
  &:focus { border-color: ${COLORS.brand}; box-shadow: 0 0 0 3px rgba(161,90,90,.15); }
`;

const Helper = styled.small`
  color: ${COLORS.dim};
  font-size: 12px;
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 0;
  background: ${COLORS.text};
  color: #fff;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: transform .15s ease, filter .15s ease, opacity .15s ease;
  &:hover { transform: translateY(-1px); filter: brightness(.96); }
  &:disabled { opacity: .6; cursor: not-allowed; transform: none; }
`;

const GhostButton = styled(Button)`
  background: #fff;
  color: ${COLORS.text};
  border: 1px solid ${COLORS.line};
    display: flex;
  justify-content: center;
  align-items: center;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const TextLink = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  color: ${COLORS.brand};
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
`;

const Divider = styled.div`
  height: 1px;
  background: ${COLORS.line};
  margin: 10px 0;
`;

const SocialWrap = styled.div`
  margin-top: 14px;
  display: grid;
  gap: 8px;
`;

const SocialRow = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
`;

const SocialButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  height: 40px;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  background: #fff;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 30px rgba(0,0,0,.08); }
  .social-icon { font-size: 16px; color: ${COLORS.brand}; }
`;

const OtpRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const OtpInput = styled(TextInput)`
  width: 120px;
  text-align: center;
  letter-spacing: 2px;
`;

const ErrorPopupWrap = styled.div`
  position: fixed;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #fff3f3, #ffe6e6);
  color: ${COLORS.brandDark};
  padding: 10px 14px;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 3200;
  max-width: 90%;
  min-width: 260px;
  font-size: 13px;
`;

const ErrorClose = styled.button`
  width: 26px; height: 26px;
  border-radius: 50%;
  border: 0; background: ${COLORS.brandDark};
  color: #fff; font-size: 16px; line-height: 26px;
  cursor: pointer;
`;

const CardsBox = styled.div`
  margin-top: 10px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const CardsHeader = styled.div`
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${COLORS.subtle};
  border-bottom: 1px solid ${COLORS.line};
`;

const CardsTitle = styled.div`
  font-weight: 900;
  font-size: 13px;
  color: ${COLORS.text};
`;

const CardRow = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid ${COLORS.line};
  &:last-child { border-bottom: 0; }
`;

const CardLeft = styled.div`
  display: grid;
  gap: 2px;
`;

const CardName = styled.div`
  font-weight: 900;
  color: ${COLORS.text};
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 900;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(161,90,90,.12);
  color: ${COLORS.brandDark};
  border: 1px solid rgba(161,90,90,.25);
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: ${COLORS.dim};
`;

const TinyBtn = styled.button`
  height: 32px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  color: ${COLORS.text};
  font-weight: 900;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); }
  &:disabled { opacity: .55; cursor: not-allowed; transform: none; box-shadow: none; }
`;


const ErrorPopup = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <ErrorPopupWrap>
      <span style={{ flex: 1 }}>{message}</span>
      <ErrorClose onClick={onClose} title="Close">×</ErrorClose>
    </ErrorPopupWrap>
  );
};

/* ================= Component ================= */
const RegisterUserMobile = ({ isRegistering: initialIsRegistering, toggleModal }) => {
  const [formData, setFormData] = useState({
    name: "", surname: "", email: "", phone: "",
    password: "", confirmPassword: "",
    day: "", month: "", year: "",
    phone_country_iso: "GE",
    phone_country_code: "995",
  });

  const parseBirth = (dateStr) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return { birth_date: "", day: "", month: "", year: "" };
    const [y, m, d] = dateStr.split("-");
    return { birth_date: dateStr, day: d, month: m, year: y };
  };

  const [isRegistering, setIsRegistering] = useState(initialIsRegistering);
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const { t, language } = useLanguage();
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [popupError, setPopupError] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const birth = parseBirth(birthDate);

  // SMS/OTP
  const [smsVerified, setSmsVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [requestInFlight, setRequestInFlight] = useState(false);
  const [verifyInFlight, setVerifyInFlight] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

    // Saved cards
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(0);
  /* ================= Validation ================= */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  // Resend complete registration link (Login view)
const [resendOpen, setResendOpen] = useState(false);
const [resendLoading, setResendLoading] = useState(false);
const [resendMsg, setResendMsg] = useState("");

  const navigate = useNavigate();
  const location = useLocation();



  const fetchCards = async () => {
    if (!user) return;
    setCardsLoading(true);
    try {
      const res = await api.get(`${API_BASE_URL}/payment_methods/list.php`, { withCredentials: true });
      setCards(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      setCards([]);
    } finally {
      setCardsLoading(false);
    }
  };

  useEffect(() => {
  if (user) fetchCards();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user?.email, userDetails?.id]);


  const deleteCard = async (id) => {
    if (!id) return;
    const ok = window.confirm(language === "ka" ? "წავშალოთ ეს ბარათი?" : "Delete this card?");
    if (!ok) return;

    setDeletingId(id);
    try {
      const res = await api.post(`${API_BASE_URL}/payment_methods/delete.php`, { id }, { withCredentials: true });
      if (res.data?.success) {
        setCards((prev) => prev.filter((c) => c.id !== id));
        setPopupError(language === "ka" ? "ბარათი წაიშალა" : "Card removed");
      } else {
        setPopupError(language === "ka" ? "ვერ წაიშალა" : "Delete failed");
      }
    } catch {
      setPopupError(language === "ka" ? "სერვერის შეცდომა" : "Server error");
    } finally {
      setDeletingId(0);
    }
  };


  useEffect(() => {
    document.title = t("titles.registeruser");
  }, [language, t]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser && storedUser !== "undefined") {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      fetchUserDetails(userData.email);
    }
  }, []);

  useEffect(() => {
    if (user?.email) fetchUserDetails(user.email);
  }, [user]);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => setCooldown(s => (s > 0 ? s - 1 : 0)), 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const fetchUserDetails = async (email) => {
    try {
      const { data } = await api.post(`${API_BASE_URL}/get_user_details.php`, { email });
      if (data?.user) setUserDetails(data.user);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const COUNTRIES = [
    { iso: "GE", name: "Georgia (+995)", dial: "995", min: 9, max: 9 },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(s => ({ ...s, [name]: value }));
  };


  const validateForm = () => {
    const errors = [];
    if (!emailRegex.test(formData.email)) {
      errors.push(language === "ka" ? "გთხოვთ შეიყვანოთ სწორი იმეილი" : "Please enter a valid email");
    }
    const c = COUNTRIES.find(x => x.iso === formData.phone_country_iso) || COUNTRIES[0];
    if (!/^\d+$/.test(formData.phone)) {
      errors.push(language === "ka" ? "ტელეფონის ნომერი უნდა იყოს მხოლოდ ციფრები" : "Phone must be digits only");
    } else if (formData.phone.length < c.min || formData.phone.length > c.max) {
      errors.push(language === "ka"
        ? `ტელეფონის სიგრძე უნდა იყოს ${c.min}-${c.max} ციფრი`
        : `Phone length must be ${c.min}-${c.max} digits`);
    }
    if (!passwordRegex.test(formData.password)) {
      errors.push(language === "ka"
        ? "პაროლი უნდა შეიცავდეს დიდ ასოს, ციფრს, სიმბოლოს და იყოს მინიმუმ 8 სიმბოლო"
        : "Password must include uppercase letter, number, symbol and be at least 8 characters");
    }
    if (formData.password !== formData.confirmPassword) {
      errors.push(language === "ka" ? "პაროლი და გამეორება არ ემთხვევა" : "Password and confirmation do not match");
    }
    if (!birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      errors.push(language === "ka" ? "შეიყვანეთ დაბადების თარიღი (YYYY-MM-DD)" : "Enter birth date (YYYY-MM-DD)");
    } else {
      const dt = new Date(birthDate);
      if (Number.isNaN(+dt)) {
        errors.push(language === "ka" ? "დაბადების თარიღი არასწორია" : "Invalid birth date");
      }
    }
    return errors;
  };

  /* ================= Server error mapping ================= */
  const normalizeServerPayload = (maybe) => {
    if (maybe && typeof maybe === "object") return maybe;
    if (typeof maybe === "string") { try { return JSON.parse(maybe); } catch { return { error: maybe }; } }
    return { error: String(maybe || "") };
  };
  const mapServerErrorToLocale = (rawPayload, lang) => {
    const p = normalizeServerPayload(rawPayload);
    const code = p?.error_code ? String(p.error_code) : "";
    const raw  = p?.error ? String(p.error) : "";
    const e    = raw.toLowerCase();

    switch (code) {
      case "EMAIL_EXISTS":      return lang === "ka" ? "მითითებული იმეილი უკვე არსებობს" : "Email already exists";
      case "PHONE_EXISTS":      return lang === "ka" ? "მითითებული ტელეფონის ნომერი უკვე არსებობს" : "Phone number already exists";
      case "INVALID_EMAIL":     return lang === "ka" ? "არასწორი ელფოსტა" : "Invalid email";
      case "INVALID_PHONE":     return lang === "ka" ? "ტელეფონის ნომერი არასწორია" : "Invalid phone number";
      case "INVALID_BIRTHDATE": return lang === "ka" ? "არასწორი დაბადების თარიღი" : "Invalid birth date";
      case "WEAK_PASSWORD":     return lang === "ka" ? "სუსტი პაროლი" : "Weak password";
      case "SAVE_FAILED":       return lang === "ka" ? "ვერ შევინახეთ მონაცემები" : "Failed to save user.";
      case "BAD_JSON":          return lang === "ka" ? "არასწორი მონაცემები" : "Invalid input";
      default: break;
    }
    if (e.includes("email already")) return lang === "ka" ? "მითითებული იმეილი უკვე არსებობს" : "Email already exists";
    if (e.includes("phone") && e.includes("exist")) return lang === "ka" ? "მითითებული ტელეფონის ნომერი უკვე არსებობს" : "Phone number already exists";
    if (e.includes("invalid email")) return lang === "ka" ? "არასწორი ელფოსტა" : "Invalid email";
    if (e.includes("invalid phone")) return lang === "ka" ? "ტელეფონის ნომერი არასწორია" : "Invalid phone number";
    return lang === "ka" ? "რეგისტრაცია ვერ განხორციელდა" : "Registration failed.";
  };

  /* ================= Helpers ================= */
  const buildE164 = () => {
    const digits = (formData.phone || "").replace(/\D+/g, "");
    const cc = (formData.phone_country_code || "").replace(/\D+/g, "");
    if (!cc || !digits) return "";
    return `+${cc}${digits}`;
  };

  const requestSms = async () => {
    if (cooldown > 0 || requestInFlight) return;
    const c = COUNTRIES.find(x => x.iso === formData.phone_country_iso) || COUNTRIES[0];

    if (!/^\d+$/.test(formData.phone) || formData.phone.length < c.min || formData.phone.length > c.max) {
      setPopupError(language === "ka" ? "შეიყვანეთ სწორი ტელეფონის ნომერი" : "Enter a valid phone number");
      return;
    }

    const phoneE164 = buildE164();
    if (!/^\+\d{9,15}$/.test(phoneE164)) {
      setPopupError(language === "ka" ? "ტელეფონი არასწორია" : "Invalid phone");
      return;
    }

    try {
      setRequestInFlight(true);
      const { data } = await api.post(
        `${API_BASE_URL}/auth/request_sms_code.php`,
        { phone: phoneE164 },
        { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
      );
      if (data?.ok) {
        setCooldown(60);
        setPopupError(language === "ka" ? "კოდი გაიგზავნა SMS-ით" : "Verification code sent");
      } else {
        setPopupError(data?.msg || (language === "ka" ? "SMS გაგზავნა ვერ მოხერხდა" : "Failed to send SMS"));
      }
    } catch {
      setPopupError(language === "ka" ? "ქსელის შეცდომა" : "Network error");
    } finally {
      setRequestInFlight(false);
    }
  };

  const verifySms = async () => {
    if (!otp || otp.length < 4 || verifyInFlight) {
      setPopupError(language === "ka" ? "შეიყვანეთ მიღებული კოდი" : "Enter the received code");
      return;
    }
    const phoneE164 = buildE164();
    try {
      setVerifyInFlight(true);
      const { data } = await api.post(
        `${API_BASE_URL}/auth/verify_sms_code.php`,
        { phone: phoneE164, code: otp },
        { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
      );
      if (data?.ok) {
        setSmsVerified(true);
        setPopupError(language === "ka" ? "ნომერი დადასტურებულია" : "Phone number verified");
      } else {
        setSmsVerified(false);
        setPopupError(data?.msg || (language === "ka" ? "კოდი არასწორია ან ვადაგასულია" : "Code is invalid or expired"));
      }
    } catch {
      setPopupError(language === "ka" ? "ქსელის შეცდომა" : "Network error");
    } finally {
      setVerifyInFlight(false);
    }
  };

  /* ================= Submit handlers ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (errors.length > 0) {
      setPopupError(errors[0]);
      return;
    }

    if (!smsVerified) {
      setPopupError(language === "ka" ? "გთხოვთ დაადასტურეთ ტელეფონის ნომერი SMS-ით" : "Please verify your phone via SMS first");
      return;
    }

    try {
      const { data } = await api.post(
        `${API_BASE_URL}/register_user.php`,
        {
          ...formData,
          phone_country_iso: formData.phone_country_iso,
          phone_country_code: formData.phone_country_code,
          phone: formData.phone,
          phone_e164: buildE164(),
          phone_verified: true,
          lang: language,
          birth_date: birth.birth_date,
          day: birth.day, month: birth.month, year: birth.year,
        },
        { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
      );

      if (data?.success) {
        setPopupError(
          language === "ka"
            ? "გთხოვთ, შეამოწმოთ ელფოსტა და დაადასტუროთ რეგისტრაცია"
            : "Please check your email to confirm your registration"
        );
        return;
      }
      setPopupError(mapServerErrorToLocale(data, language));
    } catch (err) {
      const payload = err?.response?.data ?? err?.message ?? "";
      setPopupError(mapServerErrorToLocale(payload, language));
    }
  };

  const handleAuthentication = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`${API_BASE_URL}/login_user.php`, {
        email: formData.email, password: formData.password,
      }, { withCredentials: true });

      if (data?.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
        window.dispatchEvent(new Event("storage"));
        setUser(data.user);
        window.location.reload();
      } else {
        alert(data?.error || "Login failed.");
      }
    } catch {
      alert("Login failed.");
    }
  };

  const handleLogout = async () => {
    try {
      await api.post(`${API_BASE_URL}/logout.php`, {}, { withCredentials: true });
    } finally {
      localStorage.removeItem("user");
      window.dispatchEvent(new Event("storage"));
      setUser(null);
      window.location.reload();
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (!formData.email) { setErrorMessage("Please enter your email."); return; }
    try {
      const { data } = await api.post(`${API_BASE_URL}/forgot_password.php`, {
        email: formData.email, language
      });
      if (data?.success) {
        alert(language==="ka" ? "პაროლის აღდგენის ბმული გაიგზავნა ელფოსტაზე." : "Password reset email was sent.");
        setIsForgotPassword(false);
      } else {
        setErrorMessage(data?.error || "Failed to send reset email.");
      }
    } catch {
      setErrorMessage("Error sending password reset email.");
    }
  };

  const resendCompleteLink = async () => {
  setResendMsg("");
  setPopupError("");

  const email = (formData.email || "").trim();
  if (!emailRegex.test(email)) {
    setPopupError(t("login.invalid_email"));
    return;
  }

  setResendLoading(true);
  try {
    const r = await api.post(
      `${API_BASE_URL}/resend_complete_registration.php`,
      { email, lang: language },
      { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
    );

    if (r.data?.success) {
      // ALREADY_ACTIVE -> route to forgot password suggestion
      if (r.data?.action === "ALREADY_ACTIVE") {
        setResendMsg(r.data?.message || t("login.account_already_active"));
      } else {
        setResendMsg(r.data?.message || t("login.resend_complete_sent"));
      }
      setResendOpen(true);
    } else {
      setPopupError(r.data?.error || (language === "ka" ? "შეცდომა" : "Error"));
    }
  } catch (e) {
    setPopupError(language === "ka" ? "სერვერის შეცდომა" : "Server error");
  } finally {
    setResendLoading(false);
  }
};



  return (
    <>
      <ErrorPopup message={popupError} onClose={() => setPopupError("")} />
      <Page>
        <Container>
          <Card>
            <Header>
              <Title>
                {user
                  ? t("login.userinfo")
                  : isRegistering
                    ? (isForgotPassword ? t("login.forgotpassword") : t("login.register"))
                    : t("login.authorisation")}
              </Title>
              <Subtitle>
                {user ? "" : t("login.loginwith")}
              </Subtitle>
            </Header>

            {/* Forgot Password */}
            {isForgotPassword ? (
              <Section>
                {errorMessage && <div style={{ color: "#e11d48", fontSize: 13 }}>{errorMessage}</div>}
                <form onSubmit={handleForgotPassword} style={{ display: "grid", gap: 10 }}>
                  <Label>{t("login.email")}:</Label>
                  <TextInput type="email" name="email" value={formData.email} onChange={handleChange} required />
                  <Button type="submit">{t("login.sendresetlink")}</Button>
                  <TextLink type="button" onClick={() => setIsForgotPassword(false)}>
                    {t("login.backtologin")}
                  </TextLink>
                </form>
              </Section>
            ) : user ? (
              /* Logged-in user */
              <Section>
                <div style={{ display: "grid", gap: 6, fontSize: 14, color: COLORS.text }}>
                  <div><strong>{t("login.name")}:</strong> {user?.name}</div>
                  <div><strong>{t("login.surname")}:</strong> {userDetails?.surname}</div>
                  <div><strong>{t("login.email")}:</strong> {user?.email}</div>
                  {userDetails?.phone && <div><strong>{t("login.phone")}:</strong> {userDetails.phone}</div>}
                  {userDetails?.birth_date && <div><strong>{t("login.birthdate")}:</strong> {userDetails.birth_date}</div>}
                </div>

                                <CardsBox>
                  <CardsHeader>
                    <CardsTitle>{language==="ka" ? "დამახსოვრებული ბარათები" : "Saved cards"}</CardsTitle>
                    <div style={{ fontSize: 12, color: COLORS.dim }}>
                      {cardsLoading ? (language==="ka" ? "იტვირთება..." : "Loading...") : ""}
                    </div>
                  </CardsHeader>

                  {cardsLoading ? (
                    <div style={{ padding: 12, color: COLORS.dim, fontSize: 13 }}>
                      {language==="ka" ? "ბარათების სია იტვირთება..." : "Loading cards..."}
                    </div>
                  ) : (cards?.length ? (
                    cards.map((c) => (
                      <CardRow key={c.id}>
                        <CardLeft>
                          <CardName>
                            {(c.brand || "Card")} •••• {c.last4 || "----"}
                            {c.is_default === 1 ? <Badge>{language==="ka" ? "ძირითადი" : "Default"}</Badge> : null}
                          </CardName>
                          <CardMeta>
                            {c.exp_month && c.exp_year
                              ? `${String(c.exp_month).padStart(2,"0")}/${String(c.exp_year).slice(-2)}`
                              : (language==="ka" ? "ვადა: უცნობია" : "Expiry: n/a")}
                          </CardMeta>
                        </CardLeft>

                        <TinyBtn
                          onClick={() => deleteCard(c.id)}
                          disabled={deletingId === c.id}
                          title={language==="ka" ? "წაშლა" : "Remove"}
                        >
                          {deletingId === c.id ? (language==="ka" ? "..." : "...") : (language==="ka" ? "წაშლა" : "Remove")}
                        </TinyBtn>
                      </CardRow>
                    ))
                  ) : (
                    <div style={{ padding: 12, color: COLORS.dim, fontSize: 13 }}>
                      {language==="ka"
                        ? "დამახსოვრებული ბარათი არ გაქვთ."
                        : "You have no saved cards."}
                    </div>
                  ))}
                </CardsBox>


                <Divider />

                <LinkRow>
                  <Link to="/my-purchases">
                    <GhostButton as="div" style={{ width: 180 }}>{t("menu.mypurchases")}</GhostButton>
                  </Link>
                  {userDetails?.has_password && (
                    <Link to="/change-password" state={{ user }}>
                      <GhostButton as="div" style={{ width: 180 }}>{t("login.changepassword")}</GhostButton>
                    </Link>
                  )}
                </LinkRow>

                <Button style={{ marginTop: 8 }} onClick={handleLogout}>{t("login.logout")}</Button>
              </Section>
            ) : isRegistering ? (
              /* Registration */
              <Section>
                <form onSubmit={handleSubmit} style={{ display: "grid", gap: 10 }}>
                  <Label>{t("login.name")}:</Label>
                  <TextInput type="text" name="name" value={formData.name} onChange={handleChange} required />

                  <Label>{t("login.surname")}:</Label>
                  <TextInput type="text" name="surname" value={formData.surname} onChange={handleChange} required />

                  <Label>{t("login.email")}:</Label>
                  <TextInput type="email" name="email" value={formData.email} onChange={handleChange} required />

                  <Label>{t("login.country")}:</Label>
                  <Select
                    name="phone_country_iso"
                    value={formData.phone_country_iso}
                    onChange={(e) => {
                      const iso = e.target.value;
                      const c = COUNTRIES.find(x => x.iso === iso) || COUNTRIES[0];
                      setFormData(prev => ({ ...prev, phone_country_iso: iso, phone_country_code: c.dial }));
                      setSmsVerified(false);
                    }}
                    required
                  >
                    {COUNTRIES.map(c => <option key={c.iso} value={c.iso}>{c.name}</option>)}
                  </Select>

                  <Label>{t("login.phone")}:</Label>
                  <div style={{ display: "grid", gap: 6 }}>
                    <TextInput
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D+/g, "");
                        setFormData(prev => ({ ...prev, phone: v }));
                        setSmsVerified(false);
                      }}
                      required
                      disabled={smsVerified}
                    />
                    <Helper>{t("login.example")} {` +${formData.phone_country_code} ${formData.phone}`}</Helper>
                  </div>

                  {/* SMS flow */}
                  <OtpRow>
                    <GhostButton
                      type="button"
                      onClick={requestSms}
                      disabled={cooldown > 0 || smsVerified || requestInFlight}
                      style={{ width: "auto", padding: "0 12px" }}
                    >
                      {smsVerified
                        ? (language === "ka" ? "ნომერი დადასტურებულია" : "Verified")
                        : cooldown > 0
                          ? (language === "ka" ? `ხელახლა: ${cooldown}წმ` : `Resend in ${cooldown}s`)
                          : (language === "ka" ? "კოდის გაგზავნა" : "Send code")}
                    </GhostButton>

                    {!smsVerified && (
                      <>
                        <OtpInput
                          inputMode="numeric"
                          maxLength={6}
                          placeholder={language==="ka" ? "6-ნიშნა კოდი" : "6-digit code"}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D+/g, ""))}
                        />
                        <Button
                          type="button"
                          onClick={verifySms}
                          disabled={verifyInFlight || !otp}
                          style={{ width: "auto", padding: "0 14px" }}
                        >
                          {language === "ka" ? "დადასტურება" : "Verify"}
                        </Button>
                      </>
                    )}
                  </OtpRow>

                  <Label>{t("login.password")}:</Label>
                  <TextInput type="password" name="password" value={formData.password} onChange={handleChange} required />

                  <Label>{t("login.confirmpassword")}:</Label>
                  <TextInput type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />

                  <Label>{t("login.birthdate")}:</Label>
                  <TextInput type="date" value={birthDate} onChange={(e)=>setBirthDate(e.target.value)} />

                  <Button type="submit">{t("login.registration")}</Button>
                  <TextLink type="button" onClick={() => setIsRegistering(false)}>{t("login.alreadyregistered")}</TextLink>
                </form>
              </Section>
            ) : (
              /* Login */
              <Section>
                <form onSubmit={handleAuthentication} style={{ display: "grid", gap: 10 }}>
                  <Label>{t("login.email")}:</Label>
                  <TextInput type="email" name="email" value={formData.email} onChange={handleChange} required />

                  <Label>{t("login.password")}:</Label>
                  <TextInput type="password" name="password" value={formData.password} onChange={handleChange} required />

                  <Button type="submit">{t("login.authorisation")}</Button>

                  <LinkRow>
                    <TextLink type="button" onClick={() => setIsRegistering(true)}>{t("login.notregisteredyet")}</TextLink>
                    <TextLink type="button" onClick={() => setIsForgotPassword(true)}>{t("login.forgotpassword")}</TextLink>
                  </LinkRow>
                                  <Divider />
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 900, fontSize: 13, color: COLORS.text }}>
                      {t("login.resend_complete_title")}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.dim, lineHeight: 1.35 }}>
                      {t("login.resend_complete_hint")}
                    </div>

                    <GhostButton
                      type="button"
                      onClick={resendCompleteLink}
                      disabled={resendLoading}
                      style={{ marginTop: 4 }}
                    >
                      {resendLoading ? t("login.sending") : t("login.resend_complete_btn")}
                    </GhostButton>

                    {resendMsg ? (
                      <div style={{ marginTop: 6, color: "#0b7a2a", fontWeight: 800, fontSize: 13 }}>
                        {resendMsg}
                      </div>
                    ) : null}
                  </div>
                </form>
              </Section>
            )}

            {/* Social login */}
            {!user && (
              <>
                <Divider />
                <SocialWrap>
                  <Subtitle>{t("login.loginwith")}</Subtitle>
                  <SocialRow>
<SocialButton
  type="button"
  onClick={() => window.location.assign("/api/google_callback.php")}
>                      <FaGoogle className="social-icon" />
                      {t("login.google")}
                    </SocialButton>
                  </SocialRow>
                </SocialWrap>
              </>
            )}
          </Card>
        </Container>
      </Page>
    </>
  );
};

export default RegisterUserMobile;
